import { useMutation, useQuery } from '@apollo/client/react';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_USER_PROFILES_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { ADD_USER_PROFILE_MUTATION } from '../../integrations/allkort/graphql/gql/UserProfileMutations';
import { BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { AddUserProfileResponse } from '../../integrations/allkort/graphql/interface/UserProfile';

/**
 * useAddUserProfile
 *
 * Custom hook for adding a new user profile (sub-user) to the customer's account.
 * Fetches the current customer's ID from cache automatically.
 *
 * @example
 * ```tsx
 * const { addUserProfile, loading, error } = useAddUserProfile();
 *
 * await addUserProfile({
 *   email: 'jim@testar.se',
 *   firstName: 'Jim',
 *   identifier: '196610219990',
 *   lastName: 'Testsson',
 *   phoneNumber: '+46708856767',
 * });
 * ```
 */
export const useAddUserProfile = () => {
	const { data: customerData } = useQuery<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		{ fetchPolicy: 'cache-first' }
	);

	const [addUserProfileMutation, { loading, error, data }] = useMutation<AddUserProfileResponse>(
		ADD_USER_PROFILE_MUTATION,
		{
			refetchQueries: [{ query: GET_USER_PROFILES_QUERY }],
			awaitRefetchQueries: true,
		}
	);

	const addUserProfile = async (input: {
		email: string
		firstName: string
		identifier: string
		lastName: string
		phoneNumber: string
	}) => {
		const customerId = customerData?.currentCustomer?.id;
		if (!customerId) {
			throw new Error('Customer ID not available');
		}

		try {
			const result = await addUserProfileMutation({
				variables: {
					customerId,
					email: input.email,
					firstName: input.firstName,
					identifier: input.identifier,
					lastName: input.lastName,
					phoneNumber: input.phoneNumber,
				},
			});

			if (result.error) {
				const message = (result.error as any).graphQLErrors?.[0]?.message ?? result.error.message;
				console.error('[useAddUserProfile] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.addUserProfile?.userProfile ?? null;
		} catch (err) {
			throw err;
		}
	};

	return { addUserProfile, loading, error, data };
};
